import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { X, XCircle, Lightbulb, Loader2, Edit, Check } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Tooltip = ({ content, children }) => {
  const [show, setShow] = useState(false);
  const tooltipRef = useRef(null);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        {children}
      </div>
      {show && (
        <div
          ref={tooltipRef}
          className="absolute z-50 p-2 bg-gray-800 text-white text-sm rounded shadow-lg max-w-xs whitespace-normal -top-2 left-full ml-2"
        >
          {content}
        </div>
      )}
    </div>
  );
};

const SHItemsTable = ({
  indentID,
  userId,
}) => {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState({});
  const [showDialog, setShowDialog] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const navigate = useNavigate();
  const [itemDescriptions, setItemDescriptions] = useState({});
  const [loadingDescriptions, setLoadingDescriptions] = useState({});
  const [editingDate, setEditingDate] = useState(null);
  const isAdmin = useSelector((state) => state.auth.role === 'admin');

  const fetchItemDescription = async (itemId) => {
    if (loadingDescriptions[itemId] || itemDescriptions[itemId]) return;

    console.log("Fetching description for item:", itemId);
    setLoadingDescriptions((prev) => ({ ...prev, [itemId]: true }));

    try {
      const response = await axios.get(
        `${API_BASE_URL}/viewTable/itemdescription/${itemId}`,
        { params: { indentId: indentID } }
      );

      console.log("Description API response:", response.data);

      if (response.data && response.data.Description) {
        setItemDescriptions((prev) => ({
          ...prev,
          [itemId]: response.data.Description,
        }));
      }
    } catch (error) {
      console.error("Error fetching description:", error);
    } finally {
      setLoadingDescriptions((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  useEffect(() => {
    if (items.length > 0) {
      items.forEach((item) => {
        if (item.itemID && !itemDescriptions[item.itemID]) {
          fetchItemDescription(item.itemID);
        }
      });
    }
  }, [items]);

  const fetchItemRemarks = async (itemId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/shpage/indentitems`,
        {
          params: { indentId: indentID }
        }
      );
      
      const item = response.data.find(item => item.IndentItemID === itemId);
      if (item && item.Remarks) {
        setRemarks(item.Remarks);
      } else {
        setRemarks("");
      }
    } catch (error) {
      console.error("Error fetching remarks:", error);
      setError("Failed to load remarks");
    }
  };

  const fetchIndentItems = async () => {
    if (!indentID) return;

    try {
      const response = await axios.get(
        `${API_BASE_URL}/shpage/indentitems`,
        {
          params: { indentId: indentID },
        }
      );

      const fetchedItems = response.data
        .filter((item) => item.IndentID === indentID)
        .map((item) => ({
          indentItemID: item.IndentItemID,
          itemName: item.ItemName,
          quantity: item.Quantity,
          quantityReceived: item.QuantityReceived,
          difference: Number(item.Quantity) - Number(item.QuantityReceived),
          stock: item.Stock || 0,
          categoryID: item.CategoryID,
          itemID: item.ItemID,
          remarks: item.Remarks,
          promisedDate: item.PromisedDate,
          commitedDate: item.CommitedDate,
          accountingUnitID: item.AccountingUnitID,
          unitname: item.UnitName,
          isForceClosed: Boolean(item.ForceClosure),
        }));

      setItems(fetchedItems);
    } catch (error) {
      console.error("Error fetching indent items:", error);
      setError("Failed to load indent items");
    }
  };

  useEffect(() => {
    fetchIndentItems();
  }, [indentID]);

  const handleItemForceClose = async () => {
    if (!selectedItemId || !remarks.trim()) return;
    
    setLoading(prev => ({ ...prev, [selectedItemId]: true }));
    
    try {
      if (isEditing) {
        // Update existing remarks
        await axios.put(`${API_BASE_URL}/shpage/indentitems/remarks/${selectedItemId}`, {
          remarks: remarks.trim(),
          userId: userId
        });
      } else {
        // Create new force closure
        await axios.post(`${API_BASE_URL}/shpage/indentitems/forceClose`, {
          indentItemId: selectedItemId,
          userId: userId,
          remarks: remarks.trim()
        });
      }
      
      await fetchIndentItems();
      setShowDialog(false);
      setSelectedItemId(null);
      setRemarks("");
      setIsEditing(false);
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                         error.response?.data?.error || 
                         "Failed to process request";
      setError(errorMessage);
      console.error("Force Close/Update Error:", error.response?.data);
    } finally {
      setLoading(prev => ({ ...prev, [selectedItemId]: false }));
    }
  };

  const handleForceCloseIndent = async () => {
    try {
      await axios.post(`${API_BASE_URL}/shpage/indents/forceClose`, {
        indentId: indentID,
        userId: userId
      });
  
      navigate('/SHPage');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                         error.response?.data?.error || 
                         "Failed to force close indent";
      setError(errorMessage);
      console.error("Force Indent Closure Error:", error.response?.data);
    }
  };

  const canForceCloseIndent = () => {
    const itemsWithDifference = items.filter(item => item.difference !== 0);
    const allForceClosed = itemsWithDifference.length > 0 && 
                          itemsWithDifference.every(item => item.isForceClosed);
    return allForceClosed;
  };

  const handleDateUpdate = async (itemId, newDate) => {
    try {
      await axios.put(
        `${API_BASE_URL}/viewTable/indentitems/committed-date/${itemId}`,
        { CommitedDate: newDate },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      await fetchIndentItems();
    } catch (error) {
      console.error('Error updating committed date:', error);
      setError('Failed to update committed date');
    }
  };

  const renderDescriptionCell = (itemId) => {
    if (loadingDescriptions[itemId]) {
      return (
        <div className="w-[18px] h-[18px] mx-auto flex items-center justify-center">
          <Loader2 size={18} className="animate-spin text-blue-500" />
        </div>
      );
    }

    const description = itemDescriptions[itemId];
    return (
      <div className="flex items-center space-x-2">
        <Tooltip content={description || "No description available"}>
          <button
            className={`${
              description
                ? "text-yellow-500 hover:text-yellow-600"
                : "text-gray-400 hover:text-gray-600"
            } transition-colors duration-200`}
            onClick={() => {
              if (!description) {
                fetchItemDescription(itemId);
              }
            }}
          >
            <Lightbulb size={18} />
          </button>
        </Tooltip>
      </div>
    );
  };

  const renderDateCell = (item) => {
    if (isAdmin && editingDate?.itemId === item.itemID) {
      return (
        <div className="flex items-center justify-end space-x-2">
          <input
            type="date"
            className="border rounded px-2 py-1 text-sm"
            value={editingDate.date}
            onChange={(e) => setEditingDate(prev => ({ ...prev, date: e.target.value }))}
          />
          <button
            className="text-green-500 hover:text-green-700"
            onClick={() => {
              handleDateUpdate(item.indentItemID, editingDate.date);
              setEditingDate(null);
            }}
          >
            <Check size={16} />
          </button>
          <button
            className="text-red-500 hover:text-red-700"
            onClick={() => setEditingDate(null)}
          >
            <X size={16} />
          </button>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-end space-x-2">
        <span>{item.commitedDate}</span>
        {isAdmin && (
          <button
            className="text-blue-500 hover:text-blue-700"
            onClick={() => setEditingDate({ itemId: item.itemID, date: item.commitedDate })}
          >
            <Edit size={16} />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white shadow-md rounded-lg px-4 border border-gray-200">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
          <button 
            onClick={() => setError(null)} 
            className="float-right font-bold"
          >
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {canForceCloseIndent() && (
        <div className="mb-4 flex justify-end">
          <button
            onClick={handleForceCloseIndent}
            className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded flex items-center transition-colors duration-200"
          >
            <X className="mr-2" size={16} />
            Force Close Indent
          </button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm text-gray-600">
          <thead className="bg-gray-100 text-gray-700 uppercase">
            <tr>
              <th className="px-4 py-3 text-left w-12">S.No</th>
              <th className="px-4 py-3 text-left">Item Name</th>
              <th className="px-4 py-3 text-center w-8"></th>
              <th className="px-4 py-3 text-left">AU</th>
              <th className="px-4 py-3 text-center w-24">Quantity</th>
              <th className="px-4 py-3 text-center w-24">Quantity Received</th>
              <th className="px-4 py-3 text-center w-24">Difference</th>
              <th className="px-4 py-3 text-right">Stock</th>
              <th className="px-4 py-3 text-right">Commited Date</th>
              <th className="px-4 py-3 text-left">Remarks</th>
              <th className="px-4 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr
                key={index}
                className="border-b hover:bg-gray-50 transition-colors duration-200"
              >
                <td className="px-4 py-3 text-gray-500">{index + 1}</td>
                <td className="px-4 py-3 font-medium">{item.itemName}</td>
                <td className="px-4 py-3 text-center">
                  {renderDescriptionCell(item.itemID)}
                </td>
                <td className="px-4 py-3 font-medium">{item.unitname}</td>
                <td className="px-4 py-3 text-center">{item.quantity}</td>
                <td className="px-4 py-3 text-center">{item.quantityReceived}</td>
                <td className="px-4 py-3 text-center">{item.difference}</td>
                <td className="px-4 py-3 text-right">{item.stock}</td>
                <td className="px-4 py-3 text-right">
                  {renderDateCell(item)}
                </td>
                <td className="px-4 py-3 text-left">{item.remarks || "-"}</td>
                <td className="px-4 py-3 text-center">
                  {item.difference !== 0 ? (
                    <button
                      onClick={() => {
                        setSelectedItemId(item.indentItemID);
                        setIsEditing(item.isForceClosed);
                        if (item.isForceClosed) {
                          fetchItemRemarks(item.indentItemID);
                        } else {
                          setRemarks("");
                        }
                        setShowDialog(true);
                      }}
                      disabled={loading[item.indentItemID]}
                      className={`
                        ${loading[item.indentItemID] 
                          ? 'bg-yellow-400 cursor-wait' 
                          : 'bg-yellow-500 hover:bg-yellow-600'
                        } 
                        text-white px-3 py-1 rounded text-sm transition-colors duration-200
                      `}
                    >
                      {loading[item.indentItemID] ? 'Processing...' : 
                       item.isForceClosed ? 'Edit Remarks' : 'Force Close Item'}
                    </button>
                  ) : !item.isForceClosed ? (
                    <span className="text-green-600 font-medium text-sm">
                      No Difference
                    </span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {isEditing ? 'Edit Remarks' : 'Add Remarks for Force Closure'}
              </h3>
              <button 
                onClick={() => {
                  setShowDialog(false);
                  setSelectedItemId(null);
                  setRemarks("");
                  setIsEditing(false);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <textarea
              placeholder="Enter remarks..."
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              className="w-full min-h-32 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => {
                  setShowDialog(false);
                  setSelectedItemId(null);
                  setRemarks("");
                  setIsEditing(false);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleItemForceClose}
                disabled={!remarks.trim()}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:bg-red-300 disabled:cursor-not-allowed"
              >
                {isEditing ? 'Update Remarks' : 'Force Close'}
                </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SHItemsTable;